import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Paper,
  Stack,
  Divider,
  List,
  ThemeIcon,
  Badge,
  Loader,
  Alert,
  Center,
  Box
} from '@mantine/core';
import {
  IconCheckbox,
  IconTruckDelivery,
  IconMail,
  IconClipboard,
  IconHome2,
  IconShoppingBag,
  IconShoppingCart,
  IconAlertCircle
} from '@tabler/icons-react';
// import confetti from 'canvas-confetti';

const OrderConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState<any>(null);

  // Mock data for order confirmation
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      if (id) {
        // In a real app, this would fetch order details from the server
        setOrderDetails({
          id: id,
          date: new Date().toISOString(),
          status: 'Processing',
          total: 127.47,
          shippingMethod: 'Standard Shipping',
          estimatedDelivery: '7-10 business days',
          items: [
            {
              id: 'p1',
              name: 'Ceylon Premium Tea (Black)',
              quantity: 2,
              price: 24.99
            },
            {
              id: 'p3',
              name: 'Cinnamon Sticks (Premium Grade)',
              quantity: 3,
              price: 15.99
            }
          ],
          shippingAddress: {
            name: 'John Doe',
            street: '123 Main St, Apt 4B',
            city: 'Toronto',
            state: 'ON',
            postalCode: 'M5V 2N4',
            country: 'Canada'
          },
          paymentMethod: 'Credit Card'
        });
        setLoading(false);

        // Trigger confetti effect
        // confetti({
        //   particleCount: 100,
        //   spread: 70,
        //   origin: { y: 0.6 }
        // });
      }
    }, 1500);
  }, [id]);

  if (loading) {
    return (

      <Container size="md" py="xl">
        <Center style={{ height: 300 }}>
          <Loader size="lg" />
        </Center>
      </Container>

    );
  }

  if (!orderDetails) {
    return (

      <Container size="md" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Order not found"
          color="red"
        >
          <Text mb="md">
            We couldn't find the order you're looking for. Please check the order ID or contact customer support.
          </Text>
          <Button
            component={Link}
            to="/my-orders"
            variant="outline"
          >
            View My Orders
          </Button>
        </Alert>
      </Container>

    );
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (

    <Container size="md" py="xl">
      <Paper p="xl" radius="md" withBorder>
        <Stack mb="xl">
          <ThemeIcon
            size={80}
            radius={40}
            color="green"
          >
            <IconCheckbox size={40} />
          </ThemeIcon>

          <Title order={1} >Order Confirmed!</Title>
          <Text size="lg">
            Thank you for your purchase. Your order has been received.
          </Text>

          <Badge size="xl" mt="md">
            Order #{orderDetails.id}
          </Badge>

          <Text c="dimmed" mt="xs">
            Placed on {formatDate(orderDetails.date)}
          </Text>
        </Stack>

        <Divider my="lg" />

        <Group grow mb="xl">
          <Stack >
            <ThemeIcon size="lg" radius="xl" color="blue" variant="light">
              <IconClipboard size={20} />
            </ThemeIcon>
            <Text fw={500}>Order Status</Text>
            <Badge color="blue">{orderDetails.status}</Badge>
          </Stack>

          <Stack >
            <ThemeIcon size="lg" radius="xl" color="blue" variant="light">
              <IconTruckDelivery size={20} />
            </ThemeIcon>
            <Text fw={500}>Shipping Method</Text>
            <Text >{orderDetails.shippingMethod}</Text>
          </Stack>

          <Stack >
            <ThemeIcon size="lg" radius="xl" color="blue" variant="light">
              <IconMail size={20} />
            </ThemeIcon>
            <Text fw={500}>Confirmation Email</Text>
            <Text>Sent</Text>
          </Stack>
        </Group>

        <Divider my="lg" />

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack>
              <Title order={3}>Order Details</Title>

              {orderDetails.items.map((item: any, index: number) => (
                <Group key={index} >
                  <Box>
                    <Text>{item.name}</Text>
                    <Text size="sm" c="dimmed">Quantity: {item.quantity}</Text>
                  </Box>
                  <Text fw={500}>${(item.price * item.quantity).toFixed(2)}</Text>
                </Group>
              ))}

              <Divider my="sm" />

              <Group >
                <Text>Subtotal:</Text>
                <Text fw={500}>
                  ${orderDetails.items.reduce((acc: number, item: any) => acc + (item.price * item.quantity), 0).toFixed(2)}
                </Text>
              </Group>

              <Group >
                <Text>Shipping:</Text>
                <Text fw={500}>$12.99</Text>
              </Group>

              <Divider my="sm" />

              <Group >
                <Text fw={700}>Total:</Text>
                <Text fw={700} size="lg">${orderDetails.total.toFixed(2)}</Text>
              </Group>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack>
              <Title order={3}>Shipping Information</Title>

              <Text fw={500}>{orderDetails.shippingAddress.name}</Text>
              <Text>{orderDetails.shippingAddress.street}</Text>
              <Text>
                {orderDetails.shippingAddress.city}, {orderDetails.shippingAddress.state} {orderDetails.shippingAddress.postalCode}
              </Text>
              <Text>{orderDetails.shippingAddress.country}</Text>

              <Divider my="sm" />

              <Text fw={500}>Estimated Delivery Time:</Text>
              <Text>{orderDetails.estimatedDelivery}</Text>

              <Divider my="sm" />

              <Text fw={500}>Payment Method:</Text>
              <Text>{orderDetails.paymentMethod}</Text>
            </Stack>
          </Grid.Col>
        </Grid>

        <Divider my="lg" />

        <Title order={3} mb="md">What's Next?</Title>

        <List
          spacing="md"
          icon={
            <ThemeIcon color="blue" size={24} radius="xl">
              <IconCheckbox size={16} />
            </ThemeIcon>
          }
        >
          <List.Item>
            <Text fw={500}>Order Processing</Text>
            <Text size="sm">Your order is being processed and prepared for shipping.</Text>
          </List.Item>

          <List.Item>
            <Text fw={500}>Shipping Notification</Text>
            <Text size="sm">
              You'll receive an email with tracking information once your order ships.
            </Text>
          </List.Item>

          <List.Item>
            <Text fw={500}>Track Your Order</Text>
            <Text size="sm">
              You can track the status of your order at any time in your account.
            </Text>
          </List.Item>
        </List>

        <Group mt="xl">
          <Button
            component={Link}
            to="/my-orders"
            leftSection={<IconShoppingCart size={16} />}
            variant="outline"
          >
            View My Orders
          </Button>

          <Button
            component={Link}
            to="/products"
            leftSection={<IconShoppingBag size={16} />}
          >
            Continue Shopping
          </Button>

          <Button
            component={Link}
            to="/dashboard"
            leftSection={<IconHome2 size={16} />}
            variant="light"
          >
            Dashboard
          </Button>
        </Group>
      </Paper>
    </Container>

  );
};

// Missing Grid component, adding it here
function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', margin: '-8px' }}>
      {children}
    </div>
  );
}

function GridCol({
  children,
}: {
  children: React.ReactNode,
  span: { base: number, md: number }
}) {
  return (
    <div style={{
      width: '100%',
      padding: '8px'
    }}>
      {children}
    </div>
  );
}

Grid.Col = GridCol;

export default OrderConfirmation;
